import React, { useState, useCallback, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import Logo from '../../../components/Logo/Logo';
import Button from '../../../components/UI/Button/Button';
import Form from '../../../components/UI/Form/Form';
import CallTollFree from '../../../components/Shared/CallTollFree/CallTollFree';
import MobileAlert from '../../../components/Shared/MobileAlert/MobileAlert';
import AgentsPrompt from '../AgentsPrompt/AgentsPrompt';
import PendingSignupModal from '../PendingSignupModal/PendingSignupModal';
import ClosedAccountModal, {
  closedAccountContent,
} from '../ClosedAccountModal/ClosedAccountModal';
import EmailLoginPanel from '../EmailLoginPanel/EmailLoginPanel';
import styles from './SignInData.module.css';
import * as api from '../../../api/SignIn';
import { detectIE, detectMobile, detectBrowser } from '../../../utils/utils';
import { checkValidity } from '../../../shared/utility';

const getMessageByStatus = (status, extraData) => {
  let content = null;
  switch (status) {
    case 'registered':
      content = (
        <div className={styles.SuccessWrap}>
          <div className={styles.SuccessTitle}>Success!</div>
          <div className={styles.SuccessContent}>
            Your account is currently being set up. This can take 5-10 minutes.
            Please wait for your confirmation email before logging in to Mojo.
          </div>
        </div>
      );
      break;
    case 'busy.active':
      content = (
        <div className={styles.SuccessWrap}>
          <div className={styles.SuccessTitle}>Oops!</div>
          <div className={styles.SuccessContent}>
            It appears you currently have an Active Mojo account under Account
            Number {extraData.pin}. <br />
            Please enter your password to log in.
          </div>
        </div>
      );
      break;
    case 'subscription_canceled':
      content = (
        <div className={styles.ErrorWrap}>
          <div className={styles.ErrorContent}>
            <span>
              Account suspended; payment is required. Please contact the Owner
              of the Account for further assistance.
            </span>
          </div>
        </div>
      );
      break;
    case 'email':
      content = (
        <div className={styles.ErrorWrap}>
          <div className={styles.ErrorContent}>
            <span>Invalid login/password</span>
          </div>
        </div>
      );
      break;
    default:
      content = null;
  }

  return content;
};

// MT-1765: redirects for an account closed for a Terms of Service violation
// (AppServer logout/refusal, legacy logins) open the closed-account modal.
// Without `hold` in extraData the 180-day hold line is left out.
const closedAccountByStatus = (status, extraData) => {
  if (status === 'closed_tos') {
    return closedAccountContent({
      pin: extraData.pin,
      hold: Number(extraData.hold) === 1,
    });
  }
  if (status === 'logged_out_as_fraud') {
    return closedAccountContent({ pin: extraData.pin, hold: true });
  }
  return null;
};

const SignInData = () => {
  const [inputsScheme, setInputScheme] = useState({
    email: {
      elementType: 'input',
      elementConfig: {
        type: 'email',
        placeholder: 'Enter your email',
      },
      value: '',
      validation: {
        required: true,
        isEmail: true,
      },
      valid: false,
      error: '',
      touched: false,
      label: 'Email:',
      inputFieldWrapperStyle: {
        width: '100%',
      },
    },
    password: {
      elementType: 'input',
      elementConfig: {
        type: 'password',
        placeholder: 'Enter your password',
      },
      value: '',
      validation: {
        required: true,
        minLength: 6,
      },
      valid: false,
      error: '',
      touched: false,
      label: 'Password:',
      inputFieldWrapperStyle: {
        width: '100%',
      },
    },
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [agents, setAgents] = useState(null);
  const [submittedformData, setSubmittedformData] = useState(null);
  const [selectedAgentId, setSelectedAgentId] = useState(null);
  const [showMobileAlert, setShowMobileAlert] = useState(false);
  const [pendingSignup, setPendingSignup] = useState(null);
  const history = useHistory();
  const searchParams = new URLSearchParams(history.location.search);
  const statusSearchParam = searchParams.get('status');
  const extraDataParam = searchParams.get('extraData')
    ? JSON.parse(searchParams.get('extraData'))
    : {};

  const [ignoreStatusSearchParam, setIgnoreStatusSearchParam] = useState(false);
  const [closedAccount, setClosedAccount] = useState(() =>
    closedAccountByStatus(statusSearchParam, extraDataParam)
  );
  const params = new URLSearchParams(window.location.search);
  const mobileRetry = params.get('mr', '0');

  const fetchAgents = useCallback(async (email) => {
    setIsLoading(true);
    try {
      const response = await api.checkAgentEmail(email);
      setAgents(response.data.login_data_list);
    } catch (e) {
      setErrors(e.message);
      setIgnoreStatusSearchParam(true);
    }
    setIsLoading(false);
  }, []);

  const doLogin = useCallback(async (formData) => {
    setIsLoading(true);
    try {
      const response = await api.signIn(formData);
      window.location.href = response.data.redirect_url;
    } catch (e) {
      if (e.response) {
        if (
          e.response.headers['content-type'] === 'application/json' &&
          e.response.data.closed_account
        ) {
          setErrors({});
          setPendingSignup(null);
          setClosedAccount(e.response.data.closed_account);
        } else if (
          e.response.headers['content-type'] === 'application/json' &&
          e.response.data.pending_signup
        ) {
          // Friendly "almost ready" modal: clear any stale inline error so
          // the red error block never shows behind/around the modal.
          setErrors({});
          setPendingSignup(e.response.data.pending_signup);
        } else if (
          e.response.headers['content-type'] === 'application/json' &&
          e.response.data.errors
        ) {
          setPendingSignup(null);
          setErrors(e.response.data.errors);
        } else {
          setPendingSignup(null);
          setErrors('Network Error');
        }
        setIgnoreStatusSearchParam(true);
      } else {
        setPendingSignup(null);
        setErrors(e.message);
      }
    }
    setIsLoading(false);
  }, []);

  const clearState = useCallback(() => {
    setAgents(null);
    setSelectedAgentId(null);
    setSubmittedformData(null);
  }, []);

  const handleOnSubmit = useCallback(
    (event, formData, inputsScheme) => {
      event.preventDefault();
      // Validate the app's way so errors show under the field (red message),
      // never as the browser's native validation tooltip. Email is format-
      // checked; the password is only checked for presence here (its length is
      // the backend's job, so we never block an existing short password).
      const fieldErrors = {};
      const emailResult = checkValidity(
        formData.email,
        inputsScheme.email.validation
      );
      if (!emailResult.isValid) {
        fieldErrors.email = emailResult.error;
      }
      const passwordResult = checkValidity(formData.password, {
        required: true,
      });
      if (!passwordResult.isValid) {
        fieldErrors.password = passwordResult.error;
      }
      if (Object.keys(fieldErrors).length > 0) {
        setErrors(fieldErrors);
        return;
      }
      // setSubmittedformData(formData);
      setSubmittedformData({
        ...formData,
        screenHeight: window.screen.height,
        screenWidth: window.screen.width,
        browser: detectBrowser().name,
        version: detectBrowser().version,
        mobileRetry,
        isMobile: detectMobile(),
      });
      setInputScheme(inputsScheme);
    },
    [setInputScheme]
  );

  const handleOnAgentsPromptClose = useCallback(() => {
    clearState();
  }, [clearState]);

  const handleOnClickLoginOption = (agentId) => {
    setSelectedAgentId(agentId);
    setSubmittedformData({ ...submittedformData, pin: agentId });
  };

  const handleOnClickSignUpOption = useCallback(() => {
    history.push('/sign-up/');
  }, [history]);

  const handleOnShowMobileAlertClose = useCallback(() => {
    setShowMobileAlert(false);
  }, [setShowMobileAlert]);

  useEffect(() => {
    if (submittedformData) {
      if (!agents) {
        fetchAgents(submittedformData.email);
      } else if (selectedAgentId || (agents && agents.length <= 1)) {
        doLogin(submittedformData);
        clearState();
      }
    }
  }, [
    agents,
    submittedformData,
    selectedAgentId,
    doLogin,
    clearState,
    fetchAgents,
  ]);

  useEffect(
    () => {
      if (detectIE() && !statusSearchParam) {
        window.location.href = '/browser_select/';
      }

      if (extraDataParam.email) {
        const updatedInputsScheme = { ...inputsScheme };
        updatedInputsScheme.email.value = extraDataParam.email;
        setInputScheme(updatedInputsScheme);
      }
      if (mobileRetry !== '1') {
        setShowMobileAlert(detectMobile());
      }
    },
    // eslint-disable-next-line
    []
  );

  return (
    <div className={styles.SignInData}>
      {showMobileAlert && !closedAccount && (
        <MobileAlert onClose={handleOnShowMobileAlertClose} />
      )}
      <ClosedAccountModal
        content={closedAccount}
        onClose={() => setClosedAccount(null)}
      />
      {agents && agents.length > 1 && (
        <AgentsPrompt
          isOpen
          agents={agents}
          optionBtnLabel="Login"
          onClose={handleOnAgentsPromptClose}
          onLoginOptionClick={handleOnClickLoginOption}
          onSignUpOptionClick={handleOnClickSignUpOption}
        />
      )}
      {pendingSignup && (
        <PendingSignupModal
          isOpen
          title={pendingSignup.title}
          body={pendingSignup.body}
          onClose={() => setPendingSignup(null)}
        />
      )}
      {mobileRetry === '1' && (
        <div className={styles.MobileLogo}>
          <Logo
            isDark
            style={{
              width: '104px',
              height: '47px',
              marginBottom: '30px',
            }}
          />
        </div>
      )}
      <div className={styles.Title}>
        {mobileRetry === '1' ? (
          <>
            <div>Mojo Dialing Solutions</div>
            <div>Payment Management:</div>
          </>
        ) : (
          <>
            <div>Enter your Mojo Dialer</div>
            <div>Login Details Below:</div>
          </>
        )}
      </div>
      <div className={styles.SignInDataInner}>
        {/* <div className={styles.SubTitle}>Sign In:</div> */}
        <Form
          noValidate
          inputsScheme={inputsScheme}
          onSubmit={handleOnSubmit}
          style={{ padding: '0 64px' }}
          nonFieldErrorsStyle={{ margin: '20px 64px 0', width: 'auto' }}
          errorsScheme={errors}
        >
          <Button
            type="submit"
            isBtnNext
            style={{
              marginTop: '30px',
              borderRadius: '8px',
              background: '#0053CF',
              display: 'flex',
              justifyContent: 'center',
            }}
            isLoading={isLoading}
          >
            Submit
          </Button>
          <a className={styles.ForgotYourPin} href="/password-reset/">
            Forgot your Password?
          </a>
          <EmailLoginPanel />
        </Form>
        {statusSearchParam &&
          !ignoreStatusSearchParam &&
          getMessageByStatus(statusSearchParam, extraDataParam)}
        <CallTollFree
          style={{ textAlign: 'center', fontSize: '17px', marginTop: '134px' }}
        />
      </div>
    </div>
  );
};

export default SignInData;
